import { PlanWizardCategoryEnum } from '@chiroup/core/constants/SOAPPreferences';
import { TreatmentDisplayNames, TreatmentKeys } from '@chiroup/core/constants/treatmentKeys';
import { hasProperty } from '@chiroup/core/functions/hasProperty';
import type { Modalities } from '@chiroup/core/types/Modalities.type';
import type { Muscle } from '@chiroup/core/types/Muscle.type';
import type {
  DetailObject,
  PlanWizardSelection,
  TreatmentObject,
  Visit,
  VisitPlanKeyType,
} from '@chiroup/core/types/PatientVisit.type';
import { RehabSelectOptions } from '@chiroup/core/types/RehabOptions.type';
import type { CustomizationListItem } from '../../../../clinical-skills/CustomizationListItem.type';

const treatmentTypes = {
  manualHVLA: 'Manual HVLA',
  manualLowForce: 'Manual low force',
  directionalPreference: 'Directional Preference',
  dropTable: 'Drop table',
  flexionDistraction: 'Flexion distraction',
  instrument: 'Instrument',
} as const;

const regions: { [key in string]: string } = {
  cervicalRegion: 'Cervical region',
  thoracicRegion: 'Thoracic region',
  lumbarRegion: 'Lumbar region',
  sacralRegion: 'Sacral region',
  pelvicRegion: 'Pelvic region',
  head: 'Head',
  lowerExtremity: 'Lower extremity',
  upperExtremity: 'Upper extremity',
  ribCage: 'Rib cage',
  abdomen: 'Abdomen',
  ribs: 'Ribs',
  shoulder: 'Shoulder',
  elbow: 'Elbow',
  handAndWrist: 'Hand and wrist',
  hipAndPelvis: 'Hip and pelvis',
  knee: 'Knee',
  footAndAnkle: 'Foot and ankle',
} as const;

const treatmentNames: { [key in string]: string } = {
  [TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS]:
    TreatmentDisplayNames[TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS],
  [TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS]:
    TreatmentDisplayNames[TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS],
  [TreatmentKeys.CMT_SPINAL_5_REGIONS]: TreatmentDisplayNames[TreatmentKeys.CMT_SPINAL_5_REGIONS],
  [TreatmentKeys.CMT_EXTRASPINAL_1_PLUS]:
    TreatmentDisplayNames[TreatmentKeys.CMT_EXTRASPINAL_1_PLUS],
  [TreatmentKeys.MASSAGE_THERAPY]: TreatmentDisplayNames[TreatmentKeys.MASSAGE_THERAPY],
  [TreatmentKeys.MANUAL_THERAPY]: TreatmentDisplayNames[TreatmentKeys.MANUAL_THERAPY],
  [TreatmentKeys.ACUPUNCTURE]: TreatmentDisplayNames[TreatmentKeys.ACUPUNCTURE],
  [TreatmentKeys.ACUPUNCTURE_WITH_E_STIM]:
    TreatmentDisplayNames[TreatmentKeys.ACUPUNCTURE_WITH_E_STIM],
  [TreatmentKeys.DRY_NEEDLING_1_TO_2_MUSCLES]:
    TreatmentDisplayNames[TreatmentKeys.DRY_NEEDLING_1_TO_2_MUSCLES],
  [TreatmentKeys.DRY_NEEDLING_3_PLUS_MUSCLES]:
    TreatmentDisplayNames[TreatmentKeys.DRY_NEEDLING_3_PLUS_MUSCLES],
} as const;

type categoryItem = {
  units?: number;
  services?: string[];
  unitServices?: any;
  detailNotes?: string;
  detail?: DetailObject[];
};

const treatmentSubtypes: { [key in string]: string } = {
  triggerPointRelease: 'Trigger point release',
  crossfriction: 'Cross-friction',
  IASTM: 'IASTM',
  PNFContractRelax: 'PNF/Contract-relax',
  StrainCounterstrain: 'Strain-counterstrain',
  PinAndStretch: 'Pin and stretch',
  ManualTraction: 'Manual traction',
  Massage: 'Massage',
} as const;

const evaluationNames: { [key in string]: string } = {
  '1': 'New Patient 15-29 min',
  '2': 'New Patient 30-44 min',
  '3': 'New Patient 45-59 min',
  '4': 'Established Patient 10-19 min',
  '5': 'Established Patient 20-29 min',
  '6': 'Established Patient 30-39 min',
  '7': 'Established Patient Non-provider',
} as const;

const showTimeOutputs = [
  'Therapeutic Exercise',
  'Therapeutic Activity',
  'Neuromuscular Reeducation',
  'Manual Therapy',
  'Massage Therapy',
  'Acupuncture',
  'Acupuncture with E-Stim',
];

const billableUnitsTime: { [key: number]: string } = {
  1: '15 minutes',
  2: '30 minutes',
  3: '45 minutes',
  4: '60 minutes',
} as const;

const getPlanEvaluationAndManagementContent = (evaluations: VisitPlanKeyType): string =>
  Object.entries(evaluations).reduce((str, [evaluationId, evaluationData]) => {
    const { units } = evaluationData || {};

    if (units === undefined) return str;

    const billableUnits = units > 0 ? `(${units} unit)` : '(No billable units)';

    if (!(evaluationId in evaluationNames)) return str;

    str += `${evaluationNames[evaluationId]} ${billableUnits}`;

    return str;
  }, '');

const handleDetailTypes = (
  type: string | number | string[],
  key: string,
  muscles?: Muscle[],
  treatments?: CustomizationListItem[]
): string[] => {
  const types = Array.isArray(type) ? type : [type];

  return types.flatMap(type => {
    if (typeof type === 'number') {
      return key !== 'manualTherapy'
        ? muscles?.find(muscle => muscle.ID === type)?.name || ''
        : treatments?.find(treatment => treatment.ID === type)?.name || '';
    } else {
      if (type in treatmentTypes) {
        return treatmentTypes[type as keyof typeof treatmentTypes];
      } else {
        return type ?? '';
      }
    }
  });
};

const treatmentRehabNotes = {
  manualTherapy:
    'The indicated units and corresponding minutes of hands-on myofascial release were performed on the earlier documented hypomobile joints, connective tissues, and muscles in the spine and distinctly separate limb girdle areas. The goal of this manual therapy is to relieve discomfort and restore proper joint and muscle function. Specific techniques included joint mobilization, soft tissue mobilization, stretching, and manual traction.',
  massageTherapy:
    'The indicated units and corresponding minutes of massage therapy were performed on the earlier documented hypertonic muscles in the spine and distinctly separate limb girdle areas. Specific techniques included effleurage, petrissage and tapotement.',
  'dryNeedling1-2Muscles':
    'Dry needle insertion without injection was performed in 1-2 muscles with the goal of increasing blood flow, relieving pain, and improving movement.',
  'dryNeedling3+Muscles':
    'Dry needle insertion without injection was performed in 3 or more muscles with the goal of increasing blood flow, relieving pain, and improving movement.',
  'Therapeutic Exercise':
    "Therapeutic exercises for improving range of motion, flexibility, endurance, and strength were prescribed. I explained each exercise in detail, and the exercises were demonstrated for the indicated units and corresponding minutes. Details of specific exercises are outlined in the patient's subsequent exercise log.",
  'Neuromuscular Reeducation':
    'Therapeutic procedure, 1 or more areas, each 15 minutes; neuromuscular reeducation of movement, balance, coordination, kinesthetic sense, posture, and/or proprioception for sitting and/or standing activities.',
  'Therapeutic Activity':
    "Dynamic therapeutic activities were performed for the indicated units and corresponding minutes. These included activities to enhance functional performance across various parameters, including balance, coordination, power, strength, and range of motion. The patient's subsequent exercise log outlines details of the specific activities.",
} as const;

const getPlanTreatmentContent = (
  treatment: TreatmentObject,
  muscles: Muscle[],
  treatments: CustomizationListItem[]
): string => {
  const cmtOrder = [
    TreatmentKeys.CMT_SPINAL_1_TO_2_REGIONS,
    TreatmentKeys.CMT_SPINAL_3_TO_4_REGIONS,
    TreatmentKeys.CMT_SPINAL_5_REGIONS,
    TreatmentKeys.CMT_EXTRASPINAL_1_PLUS,
  ] as const;
  const cmtLines: { key: string; html: string }[] = [];
  const otherLines: string[] = [];

  Object.values(treatment).forEach(treatmentValue => {
    Object.keys(treatmentValue).forEach(key => {
      const treatmentName = treatmentNames[key];
      const { units } = treatmentValue[key] || {};

      if (units === undefined) return;

      let billableUnits = 'No billable units';
      let showTime = '';
      let showDescription = '';

      if (units > 0) {
        billableUnits = `${units} unit`;

        if (units > 1) {
          billableUnits += 's';
        }
      }

      if (units) {
        if (showTimeOutputs.includes(treatmentName)) {
          showTime = billableUnitsTime[units];
        }

        if (key in treatmentRehabNotes) {
          showDescription = treatmentRehabNotes[key as keyof typeof treatmentRehabNotes];
        }
      }

      const details = Object.values(treatmentValue)
        .map(value => {
          const noteStr = value?.detailNotes ? `<ul><li>Notes: ${value.detailNotes}</li></ul>` : '';

          const regionDetails = Object.values(value?.detail || {}) as {
            region?: string;
            type?: number | string[];
            subtype?: string;
            notes?: string;
          }[];

          const regionStr = regionDetails
            .map(detail => {
              const arr = [];

              if (detail?.region) arr.push(regions[detail?.region]);

              if (detail?.type !== undefined) {
                arr.push(...handleDetailTypes(detail?.type, key, muscles, treatments));
              }

              if (detail?.subtype) {
                arr.push(treatmentSubtypes[detail.subtype]);
              }

              if (detail?.notes) {
                arr.push(`Notes: ${detail.notes}`);
              }

              return `<ul><li>${arr.join(', ')}</li></ul>`;
            })
            .join('');

          return `<li>${treatmentName} (${billableUnits}${showTime ? ` - ${showTime}` : ''})${
            showDescription ? `: ${showDescription}` : ''
          }${noteStr}${regionStr}</li>`;
        })
        .join('');

      /* route CMT vs. everything else */
      if (cmtOrder.includes(key as (typeof cmtOrder)[number])) {
        cmtLines.push({ key, html: details });
      } else {
        otherLines.push(details);
      }
    });
  });

  /* sort CMTs and return final string */
  const cmtHtml = cmtOrder.map(k => cmtLines.find(d => d.key === k)?.html || '').join('');

  return `${cmtHtml}${otherLines.join('')}`;
};

const getPlanContent = (
  planObject: VisitPlanKeyType,
  allPlans: { ID: number; name?: string; services?: string[] }[]
): string => {
  if (!allPlans?.length) return '';

  return Object.entries(planObject).reduce((htmlString, [planId, planData]) => {
    // bail if there are no units (should be a redundant check, tbf)
    if (planData?.units === undefined) return htmlString;

    // cross-reference 'current' planId with allPlans and find entry
    const testName = allPlans?.find(({ ID }) => ID === Number(planId))?.name;

    // bail if there's no name
    if (!testName) return htmlString;

    const planDataUnits = Number(planData.units || 0);
    let billableUnits = 'No billable units';
    let showTime = '';
    let showDescription = '';

    if (planDataUnits > 0) {
      billableUnits = `${planDataUnits} unit`;

      // if plural
      if (planDataUnits > 1) {
        billableUnits += 's';
      }
    }

    if (planDataUnits) {
      if (showTimeOutputs.includes(testName)) {
        showTime = billableUnitsTime[planDataUnits];
      }

      if (testName in treatmentRehabNotes) {
        showDescription = treatmentRehabNotes[testName as keyof typeof treatmentRehabNotes];
      }
    }

    htmlString += `<li>${testName} (${billableUnits}${showTime ? ` - ${showTime}` : ''})${
      showDescription ? `: ${showDescription}` : ''
    }</li>`;

    return htmlString;
  }, '');
};

const checkHasUnits = (data: Record<string, unknown>): boolean => {
  if (!data) return false;

  return Object.keys(data).some(key => {
    if (typeof data[key] === 'object' && data[key] !== null) {
      if (hasProperty(data[key], 'units')) {
        return true;
      } else if (checkHasUnits(data[key] as Record<string, unknown>)) {
        return true;
      }
    }

    return false;
  });
};

const replacePlanWizardOutput = (
  outputTemplate: string,
  units: number,
  selection: PlanWizardSelection,
  item?: categoryItem,
  subCategoryKey?: string,
  muscles?: Muscle[],
  treatments?: CustomizationListItem[]
): string => {
  let processedOutput = outputTemplate;

  // Replace units placeholders
  const unitText = units === 1 ? 'unit' : 'units';
  const unitsReplacement = units === 0 ? '(No billable units)' : `(${units} ${unitText})`;
  processedOutput = processedOutput.replace(/\(insert units\)/g, unitsReplacement);

  // For modalities/rehab that use both units and minutes - format as "(X unit - Y minutes)"
  if (processedOutput.includes('(insert units/minutes)')) {
    let unitsMinutesText: string;

    if (units === 0) {
      unitsMinutesText = '(No billable units)';
    } else {
      const timeText = billableUnitsTime[units] || `${units * 15} minutes`;
      unitsMinutesText = `(${units} ${unitText} - ${timeText})`;
    }

    processedOutput = processedOutput.replace(/\(insert units\/minutes\)/g, unitsMinutesText);
  }

  // Replace CPT codes placeholder
  if (processedOutput.includes('(cpt codes)')) {
    const cptCodes: string[] = [];

    // Add codes from services array
    if (selection.services?.length) {
      cptCodes.push(...selection.services);
    }

    // Add codes from unitServices object
    if (selection.unitServices) {
      Object.values(selection.unitServices).forEach(codeArray => {
        if (Array.isArray(codeArray)) {
          cptCodes.push(...codeArray);
        }
      });
    }

    // Replace with actual CPT codes wrapped in spans or remove placeholder if no codes
    if (cptCodes.length > 0) {
      // If there's only one unit but multiple CPT codes, show only the first one
      const codesToShow = units === 1 && cptCodes.length > 1 ? [cptCodes[0]] : cptCodes;

      const cptCodeSpans = codesToShow
        .filter(code => code && code.trim() !== '') // Filter out empty codes
        .map(code => {
          const label = code.trim();

          return `<span data-type="codes" class="billing-code rounded-lg border border-gray-300 bg-gray-50 px-1" data-id="${label}" data-label="${label}" contenteditable="false" draggable="true">${label}</span>`;
        })
        .join(' ');
      processedOutput = processedOutput.replace(/\(cpt codes\)/g, cptCodeSpans);
    } else {
      // Remove the placeholder if no CPT codes are available
      processedOutput = processedOutput.replace(/\s*\(cpt codes\)/g, '');
    }
  }

  // Handle detailed notes (region/type/subtype/notes)
  let detailsHtml = '';

  if (item?.detail && typeof item.detail === 'object') {
    const detailEntries = Object.values(item.detail as any);

    if (detailEntries.length > 0) {
      const detailLines = detailEntries
        .map((entry: any) => {
          const parts: string[] = [];

          if (entry?.region) {
            parts.push(regions[entry.region] || entry.region);
          }

          if (typeof entry?.type !== 'undefined') {
            const key = subCategoryKey || (selection as any)?.subCategory || '';
            const resolved = handleDetailTypes(entry.type, key, muscles, treatments) || [];
            parts.push(...resolved.filter(Boolean));
          }

          if (entry?.subtype) {
            parts.push(treatmentSubtypes[entry.subtype] || entry.subtype);
          }

          if (entry?.notes) {
            parts.push(`Notes: ${entry.notes}`);
          }

          return parts.length ? `<li>${parts.join(', ')}</li>` : '';
        })
        .filter(line => line);

      if (detailLines.length > 0) {
        detailsHtml = '\n<ul>\n' + detailLines.join('\n') + '\n</ul>';
      }
    }
  }

  // Handle general detail notes
  if (item?.detailNotes && typeof item.detailNotes === 'string') {
    const notesLine = `<li>Notes: ${item.detailNotes}</li>`;
    const notesList = `\n<ul>\n${notesLine}\n</ul>`;
    detailsHtml = detailsHtml ? detailsHtml + notesList : notesList;
  }

  // Append details to the processed output
  if (detailsHtml) {
    processedOutput += detailsHtml;
  }

  return processedOutput;
};

const generatePlanWizardContent = (
  planWizardSelections: Record<string, PlanWizardSelection>,
  categoryData: VisitPlanKeyType,
  muscles?: Muscle[],
  treatments?: CustomizationListItem[]
): string => {
  const planWizardOutputs: string[] = [];

  // Loop through categoryData structure (which can be nested)
  Object.keys(categoryData).forEach(categoryKey => {
    const categoryItem = categoryData[categoryKey];

    // Check if this is a direct item with units (flat structure)
    if (categoryItem && typeof categoryItem === 'object' && 'units' in categoryItem) {
      const selection = planWizardSelections[categoryKey];
      const output = selection?.output || '';
      const units = categoryItem.units;

      if (output && selection && !selection.hideInPlan) {
        if (typeof units === 'undefined') {
          return;
        }

        if (units && units > 0) {
          const processedOutput = replacePlanWizardOutput(
            output,
            units,
            selection,
            categoryItem,
            (selection as any)?.subCategory,
            muscles,
            treatments
          );
          planWizardOutputs.push(processedOutput);
        } else {
          // Item is selected but has no units — replacePlanWizardOutput emits "(No billable units)"
          const processedOutput = replacePlanWizardOutput(
            output,
            0,
            selection,
            categoryItem,
            (selection as any)?.subCategory,
            muscles,
            treatments
          );
          planWizardOutputs.push(processedOutput);
        }
      }
    } else {
      // This is a nested structure (like treatments with subcategories
      Object.keys(categoryItem).forEach(itemKey => {
        const item = (categoryItem as any)[itemKey];
        const selection =
          planWizardSelections[`${categoryKey}:${itemKey}`] ?? planWizardSelections[itemKey];
        const output = selection?.output || '';
        const units = item?.units;

        if (output && selection && !selection.hideInPlan) {
          if (typeof units === 'undefined') {
            return;
          }

          if (units && units > 0) {
            const processedOutput = replacePlanWizardOutput(
              output,
              units,
              selection,
              item,
              categoryKey,
              muscles,
              treatments
            );
            planWizardOutputs.push(processedOutput);
          } else {
            // Item is selected but has no units — replacePlanWizardOutput emits "(No billable units)"
            const processedOutput = replacePlanWizardOutput(
              output,
              0,
              selection,
              item,
              categoryKey,
              muscles,
              treatments
            );
            planWizardOutputs.push(processedOutput);
          }
        }
      });
    }
  });

  return planWizardOutputs.map(output => `<li>${output}</li>`).join('');
};

const generateLegacyContentExcludingPlanWizard = (
  categoryData: VisitPlanKeyType,
  referenceData: any[],
  planWizardSelections: Record<string, PlanWizardSelection>
): string => {
  // Filter out items that are already covered by plan wizard
  const planWizardIds = new Set(Object.keys(planWizardSelections).map(id => Number(id)));

  const filteredCategoryData: VisitPlanKeyType = {};
  Object.keys(categoryData).forEach(key => {
    if (!planWizardIds.has(Number(key))) {
      filteredCategoryData[key] = categoryData[key];
    }
  });

  return getPlanContent(filteredCategoryData, referenceData);
};

interface GetWYSIWYGPlanContentProps {
  visitForm: Visit;
  modalitiesData?: {
    data: Modalities[];
  };
  testsData?: {
    data: CustomizationListItem[];
  };
  supplementsData?: {
    data: CustomizationListItem[];
    itemTaxHash: {
      [key: string]: boolean;
    };
  };
  treatmentsData?: {
    data: CustomizationListItem[];
  };
  muscles?: {
    data: Muscle[];
  };
}

export const getWYSIWYGPlanContent = ({
  visitForm,
  modalitiesData,
  testsData,
  supplementsData,
  treatmentsData,
  muscles,
}: GetWYSIWYGPlanContentProps) => {
  const planWizHash = visitForm?.planWizardCategory?.reduce(
    (obj, cat) => {
      if (cat.name === PlanWizardCategoryEnum.EvaluationManagement) {
        cat.selections?.forEach(selection => {
          obj['evaluationAndManagement'][selection.ID] = selection;
        });
      }

      if (cat.name === PlanWizardCategoryEnum.Treatments) {
        cat.selections?.forEach(selection => {
          const key = `${selection.subCategory}:${selection.ID}`;
          obj['treatments'][key] = selection;
        });
      }

      if (cat.name === PlanWizardCategoryEnum.Modalities) {
        cat.selections?.forEach(selection => {
          obj['modalities'][selection.ID] = selection;
        });
      }

      if (cat.name === PlanWizardCategoryEnum.Rehab) {
        cat.selections?.forEach(selection => {
          obj['rehab'][selection.ID] = selection;
        });
      }

      if (cat.name === PlanWizardCategoryEnum.SupplementsSupplies) {
        cat.selections?.forEach(selection => {
          obj['supplementsAndSupplies'][selection.ID] = selection;
        });
      }

      return obj;
    },
    {
      evaluationAndManagement: {},
      treatments: {},
      modalities: {},
      rehab: {},
      supplementsAndSupplies: {},
    } as {
      evaluationAndManagement: Record<string, PlanWizardSelection>;
      imagingAndTests: Record<string, PlanWizardSelection>;
      treatments: Record<string, PlanWizardSelection>;
      modalities: Record<string, PlanWizardSelection>;
      rehab: Record<string, PlanWizardSelection>;
      supplementsAndSupplies: Record<string, PlanWizardSelection>;
    }
  );

  let planStr = [
    'evaluationAndManagement',
    'imagingAndTests',
    'treatments',
    'modalities',
    'rehab',
    'supplementsAndSupplies',
  ].reduce((str, category) => {
    // bail if category is not present
    if (!visitForm?.plan || !(category in visitForm.plan)) return str;

    const categoryData = visitForm.plan[category] as VisitPlanKeyType;

    // bail if category holds no data
    if (!Object.keys(categoryData || {})?.length) return str;

    // bail if there are no sub-objects with valid `units`
    if (!checkHasUnits(categoryData)) return str;

    switch (category) {
      case 'evaluationAndManagement': {
        let evalStr = '';

        if (Object.keys(planWizHash?.evaluationAndManagement || {}).length) {
          // Use Plan Wizard selections to generate content
          evalStr = generatePlanWizardContent(
            planWizHash?.evaluationAndManagement || {},
            categoryData
          );
        } else {
          // Use legacy evaluation and management content generation
          evalStr += '<li>';
          evalStr = getPlanEvaluationAndManagementContent(categoryData);
          evalStr += '</li>';
        }

        if (evalStr.length) {
          str += `<span style="font-weight: bold;">Evaluation & Management</span><ul>`;
          str += evalStr;
          str += `</ul>`;
        }

        break;
      }
      case 'imagingAndTests': {
        const imagingStr = getPlanContent(categoryData, testsData?.data || []);

        if (imagingStr.length) {
          str += `<span style="font-weight: bold;">Imaging & Tests</span><ul>`;
          str += imagingStr;
          str += `</ul>`;
        }

        break;
      }
      case 'treatments': {
        let treatmentStr = '';

        if (Object.keys(planWizHash?.treatments || {}).length) {
          // Use Plan Wizard selections to generate content (with detail lookup resolution)
          treatmentStr = generatePlanWizardContent(
            planWizHash?.treatments || {},
            categoryData,
            muscles?.data || [],
            treatmentsData?.data || []
          );
        } else {
          // Use legacy treatment content generation
          treatmentStr = getPlanTreatmentContent(
            categoryData as TreatmentObject,
            muscles?.data || [],
            treatmentsData?.data || []
          );
        }

        if (treatmentStr.length) {
          str += `<span style="font-weight: bold;">Treatments</span><ul>`;
          str += treatmentStr;
          str += `</ul>`;
        }

        break;
      }
      case 'modalities': {
        const modalitiesOutputs: string[] = [];

        // First, generate content from Plan Wizard selections
        if (Object.keys(planWizHash?.modalities || {}).length) {
          const planWizardContent = generatePlanWizardContent(
            planWizHash?.modalities || {},
            categoryData
          );

          if (planWizardContent) {
            modalitiesOutputs.push(planWizardContent);
          }
        }

        // Then, generate content for any items not covered by Plan Wizard
        const legacyContent = generateLegacyContentExcludingPlanWizard(
          categoryData,
          modalitiesData?.data || [],
          planWizHash?.modalities || {}
        );

        if (legacyContent) {
          modalitiesOutputs.push(legacyContent);
        }

        const modalitiesStr = modalitiesOutputs.join('');

        if (modalitiesStr.length) {
          str += `<span style="font-weight: bold;">Modalities</span><ul>`;
          str += modalitiesStr;
          str += `</ul>`;
        }

        break;
      }
      case 'rehab': {
        let rehabStr = '';

        if (Object.keys(planWizHash?.rehab || {}).length) {
          // Use Plan Wizard selections to generate content
          rehabStr = generatePlanWizardContent(planWizHash?.rehab || {}, categoryData);
        } else {
          // Use legacy rehab content generation
          rehabStr = getPlanContent(categoryData, RehabSelectOptions);
        }

        if (rehabStr.length) {
          str += `<span style="font-weight: bold;">Rehab</span><ul>`;
          str += rehabStr;
          str += `</ul>`;
        }

        break;
      }
      case 'supplementsAndSupplies': {
        const supplementsOutputs: string[] = [];

        // First, generate content from Plan Wizard selections
        if (Object.keys(planWizHash?.supplementsAndSupplies || {}).length) {
          const planWizardContent = generatePlanWizardContent(
            planWizHash?.supplementsAndSupplies || {},
            categoryData
          );

          if (planWizardContent) {
            supplementsOutputs.push(planWizardContent);
          }
        }

        // Then, generate content for any items not covered by Plan Wizard
        const legacyContent = generateLegacyContentExcludingPlanWizard(
          categoryData,
          supplementsData?.data || [],
          planWizHash?.supplementsAndSupplies || {}
        );

        if (legacyContent) {
          supplementsOutputs.push(legacyContent);
        }

        const supplementsStr = supplementsOutputs.join('');

        if (supplementsStr.length) {
          str += `<span style="font-weight: bold;">Supplements & Supplies</span><ul>`;
          str += supplementsStr;
          str += `</ul>`;
        }

        break;
      }
      default:
        break;
    }

    return str;
  }, '');

  return (planStr += '<p></p><p>The patient tolerated treatment well and without incident.</p>');
};
